import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import { getValidDirectAccessToken } from '../../../auth/pcAuthBrowserFlow';

const PC_AUTH_BASE_URL = (process.env.REACT_APP_PC_AUTH_BASE_URL || 'https://auth.phishcloud.io').replace(/\/$/, '');

const PASSWORD_RULES = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireDigit: true,
};

const validatePassword = (value) => {
  const errors = [];
  if (!value || value.length < PASSWORD_RULES.minLength) {
    errors.push(`At least ${PASSWORD_RULES.minLength} characters`);
  }
  if (PASSWORD_RULES.requireUppercase && !/[A-Z]/.test(value)) {
    errors.push('At least one uppercase letter');
  }
  if (PASSWORD_RULES.requireLowercase && !/[a-z]/.test(value)) {
    errors.push('At least one lowercase letter');
  }
  if (PASSWORD_RULES.requireDigit && !/\d/.test(value)) {
    errors.push('At least one number');
  }
  return errors;
};

const ChangePasswordDialog = ({ open, onClose, onLogout }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess(false);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setError('');

    if (!currentPassword) {
      setError('Current password is required.');
      return;
    }

    const strengthErrors = validatePassword(newPassword);
    if (strengthErrors.length > 0) {
      setError(`New password requires: ${strengthErrors.join(', ')}.`);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password.');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await getValidDirectAccessToken();
      if (!token) {
        setError('Session expired. Please log in again.');
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(`${PC_AUTH_BASE_URL}/auth/password/change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await response.json() : {};

      if (!response.ok) {
        setError(data.error || data.message || 'Password change failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        if (onLogout) {
          onLogout();
        }
      }, 2000);
    } catch (err) {
      setError('Unable to reach the password change service. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" style={{ marginBottom: 16 }}>
            Password changed successfully. You will be logged out in a moment.
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" style={{ marginBottom: 16 }}>
                {error}
              </Alert>
            )}
            <TextField
              label="Current Password"
              type="password"
              fullWidth
              margin="dense"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={isSubmitting}
              autoFocus
            />
            <TextField
              label="New Password"
              type="password"
              fullWidth
              margin="dense"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isSubmitting}
              helperText="8+ characters, uppercase, lowercase, and a number"
            />
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              margin="dense"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
            />
          </>
        )}
      </DialogContent>
      {!success && (
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Change Password'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ChangePasswordDialog;
